import { AbTestChoice, allAbTests } from '@daymade/common-lib/abTests';
import clsx from 'clsx';
import ExpandySection from 'components/ExpandySection';
import { DeepReadonly } from 'next/dist/shared/lib/deep-readonly';
import { useCallback, useEffect, useState } from 'react';
import { clearAbTestMenuFlag } from 'utils/ab-test/abTestMenuFlag';
import { getAbTestNameChoices } from 'utils/analytics/abtest';

import styles from './AbTestVariantPicker.module.css';

type TestName = keyof typeof allAbTests;

const testNames = Object.keys(allAbTests) as TestName[];

const getChoices = (name: TestName) =>
  allAbTests[name] as DeepReadonly<AbTestChoice>[];

/** The key getTestGroupFromQueryOrStorage reads for a forced group. Writing it skips tracking, exactly as
 * ?test=X&testGroup=Y does */
const getOverrideKey = (name: TestName) => name;

/** The key doABTestWithDistribution writes the naturally assigned group to */
const getAssignedKey = (name: TestName) =>
  getAbTestNameChoices(name, [...getChoices(name)]);

/** A query-only href keeps the current path, and getTestGroupFromQueryOrStorage forces the group from it */
const getTestGroupHref = (name: TestName, choice: string) =>
  `?${new URLSearchParams({ test: name, testGroup: choice })}`;

const readStorage = (key: string) => {
  try {
    return localStorage.getItem(key) ?? '';
  } catch {
    return '';
  }
};

const AbTestVariantPicker = () => {
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [assigned, setAssigned] = useState<Record<string, string>>({});
  const [needsReload, setNeedsReload] = useState(false);
  const [hyperlinkMode, setHyperlinkMode] = useState(false);

  const readAll = useCallback(() => {
    const nextOverrides: Record<string, string> = {};
    const nextAssigned: Record<string, string> = {};
    for (const name of testNames) {
      nextOverrides[name] = readStorage(getOverrideKey(name));
      nextAssigned[name] = readStorage(getAssignedKey(name));
    }
    setOverrides(nextOverrides);
    setAssigned(nextAssigned);
  }, []);

  // localStorage is browser only, so it cannot be read while rendering
  useEffect(() => {
    readAll();
  }, [readAll]);

  const setOverride = (name: TestName, choice: string) => {
    if (choice) {
      localStorage.setItem(getOverrideKey(name), choice);
    } else {
      localStorage.removeItem(getOverrideKey(name));
    }
    setOverrides((current) => ({ ...current, [name]: choice }));
    setNeedsReload(true);
  };

  const clearAll = () => {
    for (const name of testNames) {
      localStorage.removeItem(getOverrideKey(name));
      localStorage.removeItem(getAssignedKey(name));
    }
    readAll();
    setNeedsReload(true);
  };

  return (
    <div className={styles.container}>
      <h2 className="daymadeTypographyMMedium">AB test variants</h2>
      <p className={styles.blurb}>
        Overrides are stored in this browser only and are not tracked. Groups
        are read when a page loads, so reload to see the change.
      </p>

      <label className={styles.hyperlinkMode}>
        <input
          type="checkbox"
          checked={hyperlinkMode}
          onChange={(event) => setHyperlinkMode(event.target.checked)}
        />
        Show as hyperlinks
      </label>

      <div className={styles.tests}>
        {testNames
          .sort((x, y) => x.localeCompare(y))
          .map((name) => {
            const overridden = Boolean(overrides[name]);
            if (hyperlinkMode) {
              return (
                <ExpandySection
                  key={name}
                  containerClassName={styles.test}
                  headerContent={(expanded) => (
                    <span className={styles.name}>
                      {expanded ? '- ' : '+ '}
                      {name}
                      {overridden && (
                        <span className={styles.forced}>forced</span>
                      )}
                    </span>
                  )}
                >
                  <ul className={styles.links}>
                    {getChoices(name).map(({ choice, weight, description }) => (
                      <li key={choice}>
                        <a
                          className={clsx(
                            styles.link,
                            overrides[name] === choice && styles.linkOn,
                          )}
                          href={getTestGroupHref(name, choice)}
                        >
                          {choice}
                        </a>
                        <br />
                        {description}
                        {weight === 0 ? ' (not being served)' : ''}
                      </li>
                    ))}
                  </ul>
                </ExpandySection>
              );
            }
            return (
              <label key={name} className={styles.test}>
                <span className={styles.name}>
                  {name}
                  {overridden && <span className={styles.forced}>forced</span>}
                </span>
                <select
                  className={clsx(styles.select, overridden && styles.selectOn)}
                  value={overrides[name] ?? ''}
                  onChange={(event) => setOverride(name, event.target.value)}
                >
                  <option value="">
                    {assigned[name]
                      ? `Assigned: ${assigned[name]}`
                      : 'Not yet assigned'}
                  </option>
                  {getChoices(name).map(({ choice, weight, description }) => (
                    <option key={choice} value={choice}>
                      {choice} - {description}
                      {weight === 0 ? ' (not being served)' : ''}
                    </option>
                  ))}
                </select>
              </label>
            );
          })}
      </div>

      <div className={styles.actions}>
        <button type="button" className={styles.button} onClick={clearAll}>
          Clear all
        </button>
        <button
          type="button"
          className={clsx(styles.button, styles.buttonPrimary)}
          onClick={() => window.location.reload()}
          disabled={!needsReload}
        >
          Reload page
        </button>
      </div>

      <button
        type="button"
        className={styles.hideMenu}
        onClick={() => {
          clearAbTestMenuFlag();
          window.location.reload();
        }}
      >
        Hide this menu on this device
      </button>
    </div>
  );
};

export default AbTestVariantPicker;
